import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import apiService from "../services/apiService";
import RouteMap from "./RouteMap";
import "../styles/RouteView.css";

const STATUS_LABEL = {
  pending: { label: "Pendiente", cls: "badge-warning" },
  in_progress: { label: "En curso", cls: "badge-info" },
  completed: { label: "Completada", cls: "badge-success" },
};

const STOP_STATUS_LABEL = {
  pending: "Pendiente",
  in_route: "En ruta",
  at_destination: "En destino",
  delivered: "Entregado",
  failed: "No entregado",
};

const RouteView = ({ selectedDate }) => {
  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [expandedStops, setExpandedStops] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadRoutes();
    setExpandedStops({});
    setSelectedRoute(null);
  }, [selectedDate]);

  const loadRoutes = async () => {
    setLoading(true);
    try {
      const raw = await apiService.getSavedRoutes(selectedDate);

      if (!raw || raw.length === 0) {
        setRoutes([]);
        setSelectedRoute(null);
        return;
      }

      const data = raw.map((route) => {
        const sortedStops = (route.stops || []).sort(
          (a, b) => a.stop_number - b.stop_number,
        );

        const deliveryStops = sortedStops
          .filter((s) => s.stop_type === "DELIVERY")
          .map((stop) => ({
            stopNumber: stop.stop_number,
            stopType: stop.stop_type,
            orderId: stop.purchase_id,
            customer: stop.customer_name || "Cliente",
            address: stop.address_full || "Sin dirección",
            district: stop.district || "",
            leaveYourOrder: stop.leave_your_order || null,
            coordinates: {
              lat: parseFloat(stop.coordinates_latitude) || 0,
              lng: parseFloat(stop.coordinates_longitude) || 0,
            },
            estimatedArrival: stop.estimated_arrival || "--:--",
            timeWindow: {
              start: stop.time_window_start || "--:--",
              end: stop.time_window_end || "--:--",
            },
            originalWindow: {
              start: stop.original_window_start || null,
              end: stop.original_window_end || null,
            },
            orderType: stop.order_type || (stop.leave_your_order ? 'DINÁMICA' : 'FIJA'),
            windowMoved: !!(stop.original_window_start && stop.time_window_start && stop.original_window_start !== stop.time_window_start),
            distanceFromPrevious: parseFloat(stop.distance_from_previous) || 0,
            travelTime: parseInt(stop.travel_time) || 0,
            status: stop.delivery_status || "pending",
          }));

        const firstDelivery = deliveryStops[0];
        const lastDelivery = deliveryStops[deliveryStops.length - 1];
        const returnStop = sortedStops.find((s) => s.stop_type === "RETURN");

        return {
          routeId: route.id,
          name: route.name,
          status: route.status,
          date: route.date,
          totalStops: route.total_stops || 0,
          totalDistance: parseFloat(route.total_distance) || 0,
          estimatedDuration: parseInt(route.estimated_duration) || 0,
          warning: route.warnings || null,
          scheduledDeparture: route.scheduled_departure || null,
          returnStop: returnStop ? {
            distanceFromPrevious: parseFloat(returnStop.distance_from_previous) || 0,
            travelTime: parseInt(returnStop.travel_time) || 0,
            estimatedArrival: returnStop.estimated_arrival || null,
          } : null,
          startTime: firstDelivery ? firstDelivery.timeWindow.start : "--:--",
          estimatedEndTime: lastDelivery
            ? lastDelivery.estimatedArrival
            : "--:--",
          stops: deliveryStops,
          // RouteMap necesita el array completo (DEPARTURE + DELIVERY + RETURN)
          allStops: sortedStops.map((s) => ({
            ...s,
            coordinates_latitude: s.coordinates_latitude,
            coordinates_longitude: s.coordinates_longitude,
          })),
        };
      });

      setRoutes(data);
      setSelectedRoute(data[0]);
    } catch (error) {
      toast.error("Error al cargar rutas");
      setRoutes([]);
      setSelectedRoute(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleStopExpand = (stopNumber) => {
    setExpandedStops((prev) => ({ ...prev, [stopNumber]: !prev[stopNumber] }));
  };

  const exportToGoogleMaps = (route) => {
    if (!route.stops.length) {
      toast.error("No hay paradas para exportar");
      return;
    }

    const stops = route.stops.slice(0, 11);
    const origin = `${stops[0].coordinates.lat},${stops[0].coordinates.lng}`;
    const destination = `${stops[stops.length - 1].coordinates.lat},${stops[stops.length - 1].coordinates.lng}`;

    const waypoints = stops
      .slice(1, -1)
      .map((s) => `${s.coordinates.lat},${s.coordinates.lng}`)
      .join("|");

    // AQUÍ ESTABA EL ERROR: Faltaba el "$" antes de {origin}
    // También cambié el dominio al oficial de Google Maps para que funcione la navegación
    let url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;

    if (waypoints) {
      url += `&waypoints=${encodeURIComponent(waypoints)}`;
    }

    window.open(url, "_blank");
  };

  const renderRouteSummaryCard = (route) => {
    const badge = STATUS_LABEL[route.status] || {
      label: route.status,
      cls: "badge-secondary",
    };
    const isActive = selectedRoute && selectedRoute.routeId === route.routeId;
    return (
      <div
        key={route.routeId}
        className={"route-summary-card" + (isActive ? " active" : "")}
        onClick={() => {
          setSelectedRoute(route);
          setExpandedStops({});
        }}
      >
        <div className='route-card-header'>
          <h4>{route.name}</h4>
          <span className={"route-status " + badge.cls}>{badge.label}</span>
        </div>
        <div className='route-card-stats'>
          <div className='stat'>
            <span>{route.totalStops} paradas</span>
          </div>
          <div className='stat'>
            <span>{route.totalDistance.toFixed(1)} km</span>
          </div>
          <div className='stat'>
            <span>{route.estimatedDuration} min</span>
          </div>
        </div>
        <div className='route-card-time'>
          <small>
            {route.startTime} — {route.estimatedEndTime}
          </small>
        </div>
      </div>
    );
  };

  const renderStopCard = (stop, index) => {
    const isExpanded = expandedStops[stop.stopNumber];
    return (
      <div key={stop.stopNumber} className='stop-card'>
        <div
          className='stop-header'
          onClick={() => toggleStopExpand(stop.stopNumber)}
        >
          <div className='stop-number'>
            <span className='stop-badge'>{stop.stopNumber}</span>
          </div>
          <div className='stop-main-info'>
            <div className='stop-customer'>{stop.customer}</div>
            <div className='stop-address'>
              {stop.address}
              {stop.district ? ` — ${stop.district}` : ""}
            </div>
            <div className='stop-type-badges'>
              {stop.orderType === 'FIJA'
                ? <span className='badge badge-fija'>🏠 FIJA</span>
                : <span className='badge badge-dinamica'>🔄 DINÁMICA</span>
              }
              {stop.windowMoved && (
                <span className='badge badge-moved' title={`Original: ${stop.originalWindow.start}–${stop.originalWindow.end}`}>
                  ⏩ movida de {stop.originalWindow.start}–{stop.originalWindow.end}
                </span>
              )}
            </div>
            {stop.leaveYourOrder && (
              <div className='stop-delivery-badge'>
                💬 {stop.leaveYourOrder}
              </div>
            )}
            <div className='stop-time-window'>
              ⏰ {stop.timeWindow.start} - {stop.timeWindow.end}
            </div>
          </div>
          <div className='stop-time-info'>
            <div className='arrival-time'>Llegada: {stop.estimatedArrival}</div>
            <div
              className={
                "stop-status-badge " +
                (stop.status === "delivered" ? "delivered" : "")
              }
            >
              {STOP_STATUS_LABEL[stop.status] || stop.status}
            </div>
          </div>
          <button className='stop-expand-btn'>{isExpanded ? "▲" : "▼"}</button>
        </div>

        {isExpanded && (
          <div className='stop-details'>
            <div className='stop-detail-grid'>
              <div className='detail-item'>
                <label>Pedido:</label>
                <span>#{stop.orderId}</span>
              </div>
              <div className='detail-item'>
                <label>Distancia desde anterior:</label>
                <span>{stop.distanceFromPrevious.toFixed(2)} km</span>
              </div>
              <div className='detail-item'>
                <label>Tiempo de viaje:</label>
                <span>{stop.travelTime} min</span>
              </div>
              <div className='detail-item'>
                <label>Coordenadas:</label>
                <span>
                  {stop.coordinates.lat.toFixed(6)},{" "}
                  {stop.coordinates.lng.toFixed(6)}
                </span>
              </div>
              <div className='detail-item'>
                <label>Tipo:</label>
                <span>{stop.orderType || '—'}</span>
              </div>
              <div className='detail-item'>
                <label>Ventana asignada:</label>
                <span>{stop.timeWindow.start} – {stop.timeWindow.end}</span>
              </div>
              {stop.windowMoved && stop.originalWindow.start && (
                <div className='detail-item'>
                  <label>Ventana original:</label>
                  <span className='original-window'>{stop.originalWindow.start} – {stop.originalWindow.end}</span>
                </div>
              )}
              {stop.leaveYourOrder && (
                <div className='detail-item detail-delivery-info'>
                  <label>Instrucciones:</label>
                  <span className='delivery-instructions'>
                    {stop.leaveYourOrder}
                  </span>
                </div>
              )}
            </div>
            <div className='stop-actions'>
              <div className='stop-actions'>
                <button
                  className='btn btn-sm btn-success'
                  onClick={() =>
                    window.open(
                      /* CORREGIDO: Agregado el "$" que faltaba en la latitud */
                      `https://www.google.com/maps/search/?api=1&query=${stop.coordinates.lat},${stop.coordinates.lng}`,
                      "_blank",
                    )
                  }
                >
                  🗺️ Google Maps
                </button>
              </div>
            </div>
          </div>
        )}

        {index < selectedRoute.stops.length - 1 && (
          <div className='stop-connector'>
            <div className='connector-line'></div>
            <div className='connector-info'>
              <span>{stop.distanceFromPrevious.toFixed(1)} km</span>
              <span>{stop.travelTime} min</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='route-view'>
      <div className='route-header'>
        <h2>Rutas del Día</h2>
        <span className='date-display'>{selectedDate}</span>
      </div>

      {loading && <div className='loading-state'>⏳ Cargando rutas...</div>}

      {!loading && routes.length === 0 && (
        <div className='empty-state-full'>
          <span className='empty-icon'>📭</span>
          <p>
            No hay rutas generadas para <strong>{selectedDate}</strong>
          </p>
          <p className='text-muted'>
            Usa el selector de fechas para ver rutas de otros días
          </p>
        </div>
      )}

      {!loading && routes.length > 0 && (
        <div className='route-content'>
          {/* Sidebar */}
          <div className='route-sidebar'>
            <h3>Ventanas ({routes.length})</h3>
            <div className='route-list'>
              {routes.map(renderRouteSummaryCard)}
            </div>
          </div>

          {/* Detalle */}
          <div className='route-main'>
            {selectedRoute && (
              <>
                <div className='route-detail-header'>
                  <div>
                    <h3>{selectedRoute.name}</h3>
                    <span className='route-date'>{selectedRoute.date}</span>
                  </div>
                  <div className='route-actions'>
                    <button
                      className='btn btn-success'
                      onClick={() => exportToGoogleMaps(selectedRoute)}
                    >
                      🗺️ Google Maps
                    </button>
                  </div>
                </div>

                {selectedRoute.warning && (
                  <div className='route-warning-banner'>
                    ⚠️ {selectedRoute.warning}
                  </div>
                )}

                <div className='route-summary'>
                  <div className='summary-stat'>
                    <span className='stat-label'>Paradas</span>
                    <span className='stat-value'>
                      {selectedRoute.totalStops}
                    </span>
                  </div>
                  <div className='summary-stat'>
                    <span className='stat-label'>Distancia</span>
                    <span className='stat-value'>
                      {selectedRoute.totalDistance.toFixed(1)} km
                    </span>
                  </div>
                  <div className='summary-stat'>
                    <span className='stat-label'>Duración</span>
                    <span className='stat-value'>
                      {selectedRoute.estimatedDuration} min
                    </span>
                  </div>
                  <div className='summary-stat'>
                    <span className='stat-label'>Horario</span>
                    <span className='stat-value'>
                      {selectedRoute.startTime} —{" "}
                      {selectedRoute.estimatedEndTime}
                    </span>
                  </div>
                </div>

                <RouteMap
                  route={{ ...selectedRoute, stops: selectedRoute.allStops }}
                />

                <div className='stops-section'>
                  <h4>Secuencia de Entregas</h4>
                  <div className='stops-list'>
                    <div className='stop-card stop-card-departure'>
                      <div className='stop-header'>
                        <div className='stop-number'>
                          <span className='stop-badge stop-badge-base'>🏠</span>
                        </div>
                        <div className='stop-main-info'>
                          <div className='stop-customer'>Almacén Central</div>
                          <div className='stop-address'>Centro de Distribución — Salida</div>
                        </div>
                        <div className='stop-time-info'>
                          <div className='arrival-time'>
                            Salida: {selectedRoute.scheduledDeparture || '--:--'}
                          </div>
                        </div>
                      </div>
                    </div>
                    {selectedRoute.stops.map((stop, index) =>
                      renderStopCard(stop, index),
                    )}
                    {selectedRoute.returnStop && (
                      <div className='stop-card stop-card-return'>
                        <div className='stop-header'>
                          <div className='stop-number'>
                            <span className='stop-badge stop-badge-return'>🏠</span>
                          </div>
                          <div className='stop-main-info'>
                            <div className='stop-customer'>Almacén Central</div>
                            <div className='stop-address'>Centro de Distribución — Retorno</div>
                            <div className='stop-address'>
                              {selectedRoute.returnStop.distanceFromPrevious.toFixed(2)} km · {selectedRoute.returnStop.travelTime} min de viaje
                            </div>
                          </div>
                          <div className='stop-time-info'>
                            <div className='arrival-time'>
                              Regreso: {selectedRoute.returnStop.estimatedArrival || '--:--'}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteView;
